import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface DiscountStore {
  discountPercent: number;
  lastUpdated: string | null;
  updatedBy: string | null;
  isLoading: boolean;

  // Actions
  setDiscountPercent: (percent: number, updatedBy?: string) => void;
  fetchDiscount: () => Promise<number>;
  calculateDiscountedPrice: (originalPrice: number) => number;
}

export const useDiscountStore = create<DiscountStore>()(
  persist(
    (set, get) => ({
      discountPercent: 40, // Default 40%
      lastUpdated: null,
      updatedBy: null,
      isLoading: false,

      setDiscountPercent: (percent: number, updatedBy?: string) => {
        const sanitized = Math.max(0, Math.min(100, Math.round(percent)));
        set({
          discountPercent: sanitized,
          lastUpdated: new Date().toISOString(),
          updatedBy: updatedBy || get().updatedBy,
        });
      },

      fetchDiscount: async () => {
        try {
          set({ isLoading: true });
          const res = await fetch('/api/settings', {
            cache: 'no-store',
          });
          if (res.ok) {
            const data = await res.json();
            if (typeof data.discountPercent === 'number') {
              const sanitized = Math.max(0, Math.min(100, Math.round(data.discountPercent)));
              set({
                discountPercent: sanitized,
                lastUpdated: data.updatedAt || null,
                updatedBy: data.updatedBy || null,
                isLoading: false,
              });
              return sanitized;
            }
          }
        } catch (error) {
          console.warn('Could not fetch latest discount percent from API:', error);
        } finally {
          set({ isLoading: false });
        }
        return get().discountPercent;
      },

      calculateDiscountedPrice: (originalPrice: number) => {
        const percent = get().discountPercent;
        if (percent <= 0) return originalPrice;
        if (percent >= 100) return 0;
        const multiplier = (100 - percent) / 100;
        return Math.floor(originalPrice * multiplier);
      },
    }),
    {
      name: 'ome-discount-storage',
      partialize: (state) => ({
        discountPercent: state.discountPercent,
        lastUpdated: state.lastUpdated,
        updatedBy: state.updatedBy,
      }),
    }
  )
);
